import { useCandidateAuth } from 'features/auth';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenantContext } from 'shared/contexts';

import { getVisibleTabs, NavigationTab } from '../utils';

export function useNavigationTabs() {
    const location = useLocation();
    const { accessToken } = useCandidateAuth();
    const tenant = useTenantContext();
    const prevTab = useRef<string | null>(null);
    
    const visibleTabs = useMemo(() => {
        return getVisibleTabs(accessToken?.fullAccess || false, tenant)
            .map(tab => ({
                ...tab,
                isNew: shouldShowIsNew(tab)
            }));
    }, [ accessToken?.fullAccess, tenant ]);

    const activeTab = useMemo(() => {
        const pathSegments = location.pathname.split('/').filter(x => x);
        const currentPathSegment = pathSegments[0] === 'candidate' ? pathSegments[1] : pathSegments[0];

        const matchingTab = visibleTabs.find(tab => {
            return (tab.path.includes(currentPathSegment));
        });
        
        return matchingTab?.id || null;
    }, [ location, visibleTabs ]);

    useEffect(() => {
        if (prevTab.current) {
            const previousTab = visibleTabs.find(tab => tab.id === prevTab.current);

            if (previousTab?.isNew && shouldShowIsNew(previousTab)) {
                localStorage.setItem(`viewedNewTab-${ prevTab.current }`, 'true');
            }
        }

        prevTab.current = activeTab;
    }, [ activeTab, visibleTabs ]);

    return {
        tabs: visibleTabs,
        activeTab,
        hasFullAccess: accessToken?.fullAccess || false
    };
}

const shouldShowIsNew = (tab: NavigationTab) => {
    if (!tab.isNew) {
        return false;
    }

    const viewedTab = localStorage.getItem(`viewedNewTab-${ tab.id }`);

    return !viewedTab;
};
